import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, of, retry, switchMap, throwError, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { ProfileDataService } from '../../shared/services/profile-data.service';
import { TelemetryService } from '../../shared/telemetry/telemetry.service';
import {
  TELEMETRY_EVENTS,
  TelemetryEventAttributes,
} from '../../shared/telemetry/telemetry-events';

type DenialReason =
  TelemetryEventAttributes[(typeof TELEMETRY_EVENTS)['PlatformAccessDenied']]['reason'];

/** Outcome of evaluating the user's AD-group memberships against the required group. */
type AccessDecision = { allowed: true } | { allowed: false; reason: DenialReason };

// Bounded retry for the AD-group lookup. getUserADGroups skips the shared retry
// interceptor, and a transient Graph failure (throttling / brief outage) would
// otherwise lock an authorized user out of the whole app. We retry only on
// transient statuses so a genuine "not a member" decision is never delayed.
const GROUP_LOOKUP_RETRY_COUNT = 2;
const GROUP_LOOKUP_RETRY_DELAY_MS = 1000;
const TRANSIENT_STATUSES = new Set([408, 429, 503, 504]);

function isTransientError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && TRANSIENT_STATUSES.has(error.status);
}

/**
 * Decides whether the user's AD group memberships grant platform access.
 *
 * `platformAccessGroup` is injected per environment at build time (the literal
 * `__PLATFORM_ACCESS_GROUP__` placeholder is token-replaced). If that value is
 * missing/blank we must NOT silently grant access to everyone in production —
 * a failed substitution would otherwise open the gate app-wide. In production
 * an unconfigured group therefore fails closed; in non-production builds the
 * bypass is kept (so local/dev without the group configured still works) but is
 * logged loudly so it can't pass unnoticed.
 */
function evaluatePlatformAccess(groups: string[]): AccessDecision {
  const requiredGroup = environment.platformAccessGroup;
  const groupConfigured = requiredGroup !== '' && requiredGroup !== undefined;

  if (!groupConfigured) {
    if (environment.production) {
      console.warn(
        '[platformAccessGuard] platformAccessGroup is not configured; denying access (fail closed).',
      );
      return { allowed: false, reason: 'group-not-configured' };
    }
    console.warn(
      '[platformAccessGuard] platformAccessGroup is not configured; granting access (non-production bypass).',
    );
    return { allowed: true };
  }

  if (!groups || groups.length === 0) {
    return { allowed: false, reason: 'not-member' };
  }

  return groups.some((g) => g === requiredGroup)
    ? { allowed: true }
    : { allowed: false, reason: 'not-member' };
}

export const platformAccessGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const profileDataService = inject(ProfileDataService);
  const telemetry = inject(TelemetryService);
  const router = inject(Router);

  // Emits a telemetry event with the denial reason, then returns the redirect.
  // Centralizing this ensures no denial path redirects silently (F8).
  const deny = (reason: DenialReason): UrlTree => {
    telemetry.trackEvent(TELEMETRY_EVENTS.PlatformAccessDenied, { reason });
    return router.createUrlTree(['/no-platform-access']);
  };

  // Wait until MSAL has finished restoring/resolving the account. On a fresh
  // load (cache cleared) the account is populated asynchronously, so reading it
  // immediately would see `null` and wrongly redirect to /no-platform-access.
  return authService.whenReady().pipe(
    switchMap(() => {
      const email = authService.getEmail();
      if (!email) {
        return of(deny('no-account'));
      }
      let ids = [environment.platformAccessGroup];
      return profileDataService.getPlatformAccessGroup(ids).pipe(
        retry({
          count: GROUP_LOOKUP_RETRY_COUNT,
          delay: (error: unknown, retryCount: number) =>
            isTransientError(error)
              ? timer(GROUP_LOOKUP_RETRY_DELAY_MS * retryCount)
              : throwError(() => error),
        }),
        map((response: { value: string[] }) => {
          const decision = evaluatePlatformAccess(response.value ? response.value : []);
          return decision.allowed ? true : deny(decision.reason);
        }),
        catchError(() => of(deny('graph-error'))),
      );
    }),
  );
};
